import type { FlexProps } from '@chakra-ui/react';
import { Flex } from '@chakra-ui/react';
import { InfoWithLoading } from '@ui/components/Info';
import { COIN_MODE } from '@ui/hooks/useCoinMode';
import { useCoinUsdValue } from '@ui/hooks/useCoinUsdValue';
import { useVnoUsdValue } from '@ui/hooks/useVnoUsdValue';
import { useWcroUsdValue } from '@ui/hooks/useWcroUsdPrice';
import { useTranslations } from '@ui/i18n';
import { formatPendingRewards, formatTokenAmount } from '@ui/utils/format';
import type { VaultType } from '@ui/views/Vno/ReservoirAndFountain/types';
import numbro from 'numbro';

import { useVenoFountainData, useVenoReservoirData } from './useVenoInfoData';

export type VaultsInfoProps = FlexProps & {
  vaultType: VaultType;
};
export const VaultsInfo = ({ vaultType, ...props }: VaultsInfoProps) => {
  const t = useTranslations();

  const isFountain = vaultType === 'FOUNTAIN';

  const fountainData = useVenoFountainData(isFountain);
  const fountainVnoUsdData = useVnoUsdValue(fountainData.pendingRewards);

  const reservoirData = useVenoReservoirData(!isFountain);
  const reservoirWcroUsdData = useWcroUsdValue(
    reservoirData.pendingRewardsMap.wcro ?? undefined,
  );
  const reservoirAtomUsdData = useCoinUsdValue(
    reservoirData.pendingRewardsMap.atom ?? undefined,
    'atom',
  );
  const reservoirWethUsdData = useCoinUsdValue(
    reservoirData.pendingRewardsMap.weth ?? undefined,
    'eth',
  );

  const isLoading = isFountain
    ? fountainData.isLoading
    : reservoirData.isLoading;
  const apr = isFountain ? fountainData.averageApr : reservoirData.averageApr;
  const lockedVno = isFountain
    ? fountainData.lockedVno
    : reservoirData.lockedVno;
  const unlockedVno = isFountain
    ? fountainData.unlockedVno
    : reservoirData.unlockedVno;

  return (
    <>
      <Flex direction="column" gap="16px" {...props}>
        <InfoWithLoading
          isLoading={isLoading}
          title={t('Average APR')}
          value={
            apr !== undefined
              ? numbro(apr).format({
                  output: 'percent',
                  mantissa: 2,
                  thousandSeparated: true,
                })
              : undefined
          }
        />
        <InfoWithLoading
          isLoading={isLoading}
          title={t('{coin} locked', {
            coin: 'VNO',
          })}
          value={formatTokenAmount(lockedVno, 2) ?? undefined}
        />
        <InfoWithLoading
          isLoading={isLoading}
          title={t('{coin} unlocked', {
            coin: 'VNO',
          })}
          value={formatTokenAmount(unlockedVno, 2) ?? undefined}
        />
        {isFountain ? (
          <InfoWithLoading
            isLoading={isLoading}
            title={t('Unclaimed {coin} rewards', {
              coin: 'VNO',
            })}
            value={`${formatPendingRewards(fountainData.pendingRewards)} VNO`}
            convertedValue={`${formatTokenAmount(
              fountainVnoUsdData.data,
              2,
            )} USD`}
            convertedValueLoading={fountainVnoUsdData.isInitialLoading}
          />
        ) : (
          <>
            {reservoirData.pendingRewardsMap.wcro !== null &&
              reservoirData.pendingRewardsMap.atom && (
                <>
                  <InfoWithLoading
                    isLoading={isLoading}
                    title={t('Unclaimed {coin} rewards', {
                      coin: 'WCRO',
                    })}
                    value={`${formatPendingRewards(
                      reservoirData.pendingRewardsMap.wcro,
                    )} WCRO`}
                    convertedValue={`${formatTokenAmount(
                      reservoirWcroUsdData.data,
                      2,
                    )} USD`}
                    convertedValueLoading={
                      reservoirWcroUsdData.isInitialLoading
                    }
                  />
                  <InfoWithLoading
                    isLoading={isLoading}
                    title={t('Unclaimed {coin} rewards', {
                      coin: 'ATOM',
                    })}
                    value={`${formatPendingRewards(
                      reservoirData.pendingRewardsMap.atom,
                      undefined,
                      COIN_MODE.atom.decimals,
                    )} ATOM`}
                    convertedValue={`${formatTokenAmount(
                      reservoirAtomUsdData.data,
                      2,
                    )} USD`}
                    convertedValueLoading={reservoirAtomUsdData.isLoading}
                  />
                </>
              )}
          </>
        )}
        {!isFountain && reservoirData.pendingRewardsMap.weth !== null && (
          <>
            <InfoWithLoading
              isLoading={isLoading}
              title={t('Unclaimed {coin} rewards', {
                coin: 'WETH',
              })}
              value={`${formatPendingRewards(
                reservoirData.pendingRewardsMap.weth,
              )} WETH`}
              convertedValue={`${formatTokenAmount(
                reservoirWethUsdData.data,
                2,
              )} USD`}
              convertedValueLoading={reservoirWethUsdData.isLoading}
            />
          </>
        )}
      </Flex>
    </>
  );
};
